import { motion } from "motion/react";
import type { ReactNode } from "react";

import { usePrefersReducedMotion } from "../lib/usePrefersReducedMotion";
import { inView, rise, stagger } from "./home/motion";
import styles from "./Showcase.module.css";

/**
 * One feature, shown beside its own words. `side` is passed in rather than derived from
 * `:nth-child`, or inserting a section flips every block below it. `media` may be null.
 */
export type ShowcaseSide = "left" | "right";
export type ShowcaseSize = "regular" | "large" | "full";

interface ShowcaseProps {
  id?: string;
  /** Which side the media sits on from 900px up. Ignored by `full`. */
  side?: ShowcaseSide;
  /**
   * `regular` splits the row evenly, `large` gives the media the wider column,
   * `full` puts the media under the text at the full width of the section.
   */
  size?: ShowcaseSize;
  eyebrow: string;
  title: string;
  children: ReactNode;
  media?: ReactNode;
  /** Sits under the media, at the media's width. */
  mediaCaption?: ReactNode;
  /** Full width under both columns. The voice facts are the reason for it. */
  below?: ReactNode;
}

export function Showcase({
  id,
  side = "right",
  size = "regular",
  eyebrow,
  title,
  children,
  media,
  mediaCaption,
  below,
}: ShowcaseProps) {
  const reduced = usePrefersReducedMotion() ?? false;
  const split = media != null && size !== "full";

  return (
    <section className={styles.section} id={id}>
      <motion.div className={styles.inner} variants={stagger(reduced)} {...inView}>
        <div
          className={styles.row}
          data-size={size}
          data-side={split ? side : undefined}
          data-split={split || undefined}
        >
          {/* Text first in the DOM whichever side it is drawn on. The visual
              swap is a grid placement, not a `row-reverse`, so reading order
              and tab order stay the order the words are written in. */}
          <div className={styles.copy}>
            <motion.p className={styles.eyebrow} variants={rise(reduced)}>
              {eyebrow}
            </motion.p>
            <motion.h2 className={styles.title} variants={rise(reduced)}>
              {title}
            </motion.h2>
            <motion.div className={styles.body} variants={rise(reduced)}>
              {children}
            </motion.div>
          </div>

          {media != null && (
            <motion.figure className={styles.media} variants={rise(reduced)}>
              {media}
              {mediaCaption && (
                <figcaption className={styles.caption}>{mediaCaption}</figcaption>
              )}
            </motion.figure>
          )}
        </div>

        {below && (
          <motion.div className={styles.below} variants={rise(reduced)}>
            {below}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
